import type { AnyObject, CartItem } from "../interfaces";
import ApiService from "./api-service";

const baseEndpoint = "/carts";
const singleCartEndpoint = `${baseEndpoint}/:cartId`;
const cartItemsEndpoint = `${singleCartEndpoint}/items`;
const singleItemEndpoint = `${cartItemsEndpoint}/:itemId`;

function formatCartItem(item: CartItem) {
  return {
    item_id: item.id,
    item_type: item.type,
    quantity: item.quantity,
    price: item.price,
    discounted_price: item.discountedPrice,
    currency_code: item.currencyCode,
  };
}

export default class CartService {
  static async createCart() {
    return await ApiService.post(baseEndpoint);
  }

  static async getCart(cartId: string) {
    return await ApiService.get(singleCartEndpoint, { cartId });
  }

  static async emptyCart(cartId: string) {
    return await ApiService.put(`${singleCartEndpoint}/empty`, { cartId });
  }

  static async updateCart(cartId: string, updateData: { ownerId?: string, status?: string }) {
    return await ApiService.put(singleCartEndpoint, { cartId }, updateData);
  }

  static async addItemToCart(cartId: string, item: CartItem) {
    return await ApiService.post(
      cartItemsEndpoint,
      { cartId },
      formatCartItem(item) as unknown as AnyObject
    );
  }

  static async addItemsToCart(cartId: string, items: CartItem[]) {
    return await ApiService.post(
      cartItemsEndpoint,
      { cartId },
      { items: items.map(formatCartItem) }
    );
  }

  static async removeItemFromCart(cartId: string, itemId: string) {
    return await ApiService.delete(singleItemEndpoint, { cartId, itemId });
  }

  static async updateItemInCart(cartId: string, itemId: string, updateData: CartItem) {
    const { price, discountedPrice } = updateData;

    return await ApiService.put(
      singleItemEndpoint,
      { cartId, itemId },
      { price, discounted_price: discountedPrice }
    );
  }

  static async incrementItemQuantityInCart(cartId: string, itemId: string, incrementNumber: number) {
    const endpoint = `${singleItemEndpoint}/incrementCount?quantity=:quantity`;

    return await ApiService.put(
      endpoint,
      { cartId, itemId, quantity: incrementNumber }
    );
  }

  static async decrementItemQuantityInCart(cartId: string, itemId: string, decrementNumber: number) {
    const endpoint = `${singleItemEndpoint}/decrementCount?quantity=:quantity`;

    return await ApiService.put(
      endpoint,
      { cartId, itemId, quantity: decrementNumber }
    );
  }

  static async changeItemQuantityInCart(cartId: string, itemId: string, quantity: number) {
    const endpoint = `${singleItemEndpoint}/updateCount?quantity=:quantity`;

    return await ApiService.put(endpoint, { cartId, itemId, quantity });
  }

  static async getItemsInCart(cartId: string) {
    return await ApiService.get(cartItemsEndpoint, { cartId });
  }
}
